/**
 * SelectionSummaryHelper
 * 
 * Static helper methods for building and formatting selection summaries.
 * 
 * Methods:
 * 1. transformData() - Transform data list into formatted key-value strings
 * 2. replacePipe() - Replace pipe characters with colon-space separator
 * 3. buildSelectionSummaryText() - Build selection summary text with all sections
 * 4. parseSelectionSummary() - Parse selection summary text into sections
 */

export class SelectionSummaryHelper {
  
  /**
   * EXACT COPY from chatbotexpand.component.ts (lines 2545-2592)
   * Transform data list into formatted key-value strings
   */
  static transformData(dataList: string[]): string[] {
    const transformedData: { [key: string]: { [subKey: string]: string[] } } =
      {};

    dataList.forEach((item) => {
      const parts = item.split('|');
      const mainKey = parts[0];

      if (parts.length > 1) {
        const subKey = parts.slice(1, -1).join('|');
        const value = parts.slice(-1)[0];

        if (!transformedData[mainKey]) {
          transformedData[mainKey] = {};
        }

        if (!transformedData[mainKey][subKey]) {
          transformedData[mainKey][subKey] = [];
        }

        transformedData[mainKey][subKey].push(value);
      } else {
        // Handle single-value case
        if (!transformedData[mainKey]) {
          transformedData[mainKey] = { '': [] };
        }

        transformedData[mainKey][''].push(parts[0]);
      }
    });

    return Object.keys(transformedData).map((mainKey) => {
      const subKeys = Object.keys(transformedData[mainKey]);
      const subStrings = subKeys.map((subKey) => {
        const subString =
          subKey === ''
            ? transformedData[mainKey][subKey]
            : `${subKey}: ${transformedData[mainKey][subKey].join(',')}`;
        return subString;
      });
      return `${mainKey}: ${subStrings.join(',').replace(/,/g, ' , ')}`;
    });
  }

  /**
   * EXACT COPY from chatbotexpand.component.ts (lines 1631-1633)
   * Replace pipe characters with colon-space separator
   */
  static replacePipe(input: string): string {
    return input.replace(/\|/g, ' : ');
  }

  /**
   * EXACT COPY from chatbotexpand.component.ts (lines 2462-2542)
   * Build selection summary text with all sections
   */
  static buildSelectionSummaryText(
    RowValues: string[],
    RowNestingValues: string[],
    ColumnValues: string[],
    ColumnNestingValues: string[],
    FilterData: string[],
    isSavedSelection: boolean,
    config: {
      selectedDataFormat: string;
      DefaultSampleSize: string;
      SignificanceVsReference: string;
      DefaultConfidenceLevel: string;
      selectedIndex: string;
      isIndex: boolean;
      DefaultWeight: string;
      isTotalBase: boolean;
      selected_study: string;
      isFilteringActive: boolean;
      BASE_TYPES: { TOTAL: string; ANSWER: string };
    }
  ): string {
    let rowNestingSection = '';
    let colNestSection = '';
    let FiltersSection = '';
    let significanceSection = '';
    let confidenceSection = '';
    let Reference = '';
    let Weight = '';
    let Base = '';
    let study = '';
    
    if (RowNestingValues.length > 0) {
      rowNestingSection = ` <span class="grey-separator"> || </span> <span class="summary-label">Row Nesting:</span> ${RowNestingValues.join(
        '&nbsp;,&nbsp;'
      )}`;
    }
    if (ColumnNestingValues.length > 0) {
      colNestSection = ` <span class="grey-separator"> || </span> <span class="summary-label">Column Nesting:</span> ${ColumnNestingValues.join(
        '&nbsp;,&nbsp;'
      )}`;
    }
    if (FilterData.length > 0) {
      FiltersSection = `  <span class="summary-label">Filters:</span> ${FilterData.join(
        '&nbsp;,&nbsp;'
      )} <span class="grey-separator"> || </span> `;
    }
    if (!isSavedSelection && config.selectedDataFormat == 'Respondent Level') {
      if (config.SignificanceVsReference != '') {
        significanceSection = `  <span class="summary-label">Significance:</span> ${config.DefaultSampleSize
          } : ${SelectionSummaryHelper.replacePipe(
            config.SignificanceVsReference
          )} <span class="grey-separator"> || </span> `;
      } else {
        significanceSection = `  <span class="summary-label">Significance:</span> ${config.DefaultSampleSize} <span class="grey-separator"> || </span> `;
      }
      confidenceSection = `  <span class="summary-label">Confidence Level:</span> ${config.DefaultConfidenceLevel} <span class="grey-separator"> || </span> `;
    }
    if (
      config.selectedIndex != '' &&
      config.isIndex &&
      config.selectedDataFormat == 'Respondent Level'
    ) {
      Reference = `  <span class="summary-label">Reference:</span> ${SelectionSummaryHelper.replacePipe(
        config.selectedIndex
      )} <span class="grey-separator"> || </span> `;
    }
    if (
      config.DefaultWeight != '' &&
      config.selectedDataFormat == 'Respondent Level'
    ) {
      Weight = `  <span class="summary-label">Calculation Weight:</span> Primary Weight <span class="grey-separator"> || </span> `;
    }
    if (config.selectedDataFormat == 'Respondent Level') {
      Base = `<span class="summary-label">Calculation Base:</span> ${config.isTotalBase ? config.BASE_TYPES.TOTAL : config.BASE_TYPES.ANSWER
        } <span class="grey-separator"> || </span> `;
    }
    study = `<span class="summary-label">Study Type:</span> ${config.selected_study} <span class="grey-separator"> || </span> `;

    let selectionSummary = `${study}  ${Base} <span class="summary-label">Nesting Type:</span> ${config.isFilteringActive ? 'Filtering' : 'Profiling'
      } <span class="grey-separator"> || </span> ${Weight} ${significanceSection} ${confidenceSection} ${Reference}  ${FiltersSection} <span class="summary-label">Row:</span> ${RowValues.join(
        '&nbsp;,&nbsp;'
      )}
    ${rowNestingSection} <span class="grey-separator"> || </span> <span class="summary-label">Column:</span> ${ColumnValues.join(
        '&nbsp;,&nbsp;'
      )} ${colNestSection}`;

    selectionSummary = selectionSummary
      .replaceAll('@', ' , ')
      .replace(/<[^>]*>/g, '')           // Remove all HTML tags
      .replace(/&nbsp;/g, ' ')          // Replace &nbsp; with regular spaces
      .replace(/\s+/g, ' ')             // Replace multiple spaces with single space
      .replace(/\s*\|\|\s*/g, ' || ')   // Clean spacing around || separators
      .replace(/^\s*\|\|\s*/, '')       // Remove leading separator
      .replace(/\s*\|\|\s*$/, '')       // Remove trailing separator
      .trim();                          // Remove leading/trailing whitespace

    return selectionSummary;
  }

  /**
   * EXACT COPY from chatbotexpand.component.ts (lines 6771-6850)
   * Parse selection summary text from config service into sections
   */
  static parseSelectionSummary(
    selectionSummaryText: string
  ): {
    titleSection: { key: string; value: string }[];
    entryPoints: { key: string; value: string }[];
    selections: { key: string; value: string }[];
  } {
    const titleSection: { key: string; value: string }[] = [];
    const entryPoints: { key: string; value: string }[] = [];
    const selections: { key: string; value: string }[] = [];
    
    let selectionSummaryArray = selectionSummaryText.split("||");

    for (let i = 0; i < selectionSummaryArray.length; i++) {
      let section = selectionSummaryArray[i].trim();

      if (section.startsWith("Compare Across:")) {
        const titleContent = section.replace("Compare Across:", "").trim();
        const titleParts = titleContent.split(":");
        if (titleParts.length > 1) {
          titleSection.push({
            key: titleParts[0].trim(),
            value: titleParts.slice(1).join(":").trim()
          });
        } else {
          titleSection.push({
            key: "",
            value: titleContent
          });
        }
      }
      else if (section.startsWith("Entry Point:")) {
        const entryPointContent = section.replace("Entry Point:", "").trim();
        const entryPointItems = entryPointContent.split(";");

        for (let item of entryPointItems) {
          item = item.trim();
          if (item) {
            const itemParts = item.split(":");
            if (itemParts.length > 1) {
              entryPoints.push({
                key: itemParts[0].trim(),
                value: itemParts.slice(1).join(":").trim()
              });
            }
          }
        }
      }
      else if (section.startsWith("Selections:")) {
        let selectionsText = section.replace("Selections:", "").trim();
        let selectionItems = selectionsText.split(";");

        for (let item of selectionItems) {
          item = item.trim();
          if (item) {
            let itemParts = item.split(":");
            if (itemParts.length > 1) {
              let key = itemParts[0].trim();
              let value = itemParts.slice(1).join(":").trim();

              selections.push({
                key: key,
                value: value
              });
            } else {
              selections.push({
                key: '',
                value: item
              });
            }
          }
        }
      }
      else if (section) {
        let sectionParts = section.split(":");
        if (sectionParts.length > 1) {
          let key = sectionParts[0].trim();
          let value = sectionParts.slice(1).join(":").trim();

          selections.push({
            key: key,
            value: value
          });
        }
      }
    }

    return {
      titleSection,
      entryPoints,
      selections
    };
  }
}
